import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import Toggle from 'react-toggle';
import { DateRangePicker } from 'react-dates';
import moment from 'moment';

import { useTheme } from 'emotion-theming';
import NoHoverButton from '../../../shared/components/NoHoverButton';

import Wrapper from '../../../shared/_TBComponents/PickerModal/Wrapper';
import {
  CloseButtonContainer,
  IconContainer,
  PrimaryWrapper,
} from '../../../shared/_TBComponents/PickerModal';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import {
  ColorGray,
  ColorGreen,
  ColorWhite,
} from '../../../shared/GlobalStyles';

import useWindowSize from '../../../shared/hooks/useWindowSize';
import { journalTracker } from '../../../shared/utils/amplitudeHelper';
import { useJournalVariant } from '../JournalVariantContext';
import {
  setActiveReduxModal,
  setFilterOptions,
  setSubActiveReduxModal,
  setTempExercise,
} from '../ducks/journalActions';

const BUILD_BORDER_RADIUS = '8px';

const ActionWrapper = styled('div')`
  align-items: center;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border-bottom-left-radius: ${(props) => (props.isBuildVariant ? BUILD_BORDER_RADIUS : '12px')};
  border-bottom-right-radius: ${(props) => (props.isBuildVariant ? BUILD_BORDER_RADIUS : '12px')};
  border-top: ${(props) => (props.isBuildVariant ? 'none' : '1px solid #f0f0f0')};
  bottom: 0;
  color: ${ColorGray};
  display: flex;
  font-size: 12px;
  font-weight: 500;
  height: ${(props) => (props.isBuildVariant ? 'auto' : '50px')};
  justify-content: end;
  width: 100%;
  z-index: 1;
  ${(props) => (props.isBuildVariant ? `
    padding: 16px;
  ` : `
    .submit-button {
      align-items: center;
      appearance: none;
      background: ${ColorGreen};
      border: 0;
      border-bottom-left-radius: 12px;
      border-bottom-right-radius: 12px;
      border-top-left-radius: 0px;
      border-top-right-radius: 0px;
      color: ${ColorWhite};
      display: flex;
      font-size: 14px;
      font-weight: 900;
      height: 100%;
      justify-content: center;
      outline: none !important;
      padding: 6.5px 25px;
      text-decoration: none;
      width: 100%;
    }
  `)}
`;

const BuildPrimaryWrapper = styled(PrimaryWrapper)`
  border-radius: ${BUILD_BORDER_RADIUS};
`;

const BuildPrimaryButton = styled('button')`
  align-items: center;
  background: ${(props) => (props.disabled ? props.theme.colors.textTertiary : props.theme.colors.buttonPrimaryBg)};
  border: 1px solid ${(props) => (props.disabled ? props.theme.colors.textTertiary : props.theme.colors.buttonPrimaryBg)};
  border-radius: 4px;
  color: ${ColorWhite};
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  height: 40px;
  justify-content: center;
  user-select: none;
  width: 100%;
`;

const BuildCloseButton = styled('button')`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
`;

const ClearFilters = styled('div')`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: end;
  width: 50%;
`;

const ClearFiltersButton = styled('button')`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.surfaceElevated};
  border: none;
  color: ${(props) => (props.isBuildVariant ? props.theme.colors.textSecondary : '#a3a3a3')};
  display: flex;
  font-size: 16px;
  ${(props) => (props.isBuildVariant ? `
    font-family: 'Nunito Sans';
    font-weight: 600;
    &:hover {
      color: ${props.theme.colors.text};
    }
  ` : '')}
`;

const DateFilterContainer = styled('div')`
  margin-bottom: 16px;
  width: 100%;
`;

const DatePickerContainer = styled('div')`
  tr:first-of-type {
    .CalendarDay,
    .CalendarDay__selected {
      border-top: 1px solid ${({ theme }) => (theme.isDark ? theme.colors.borderLight : '#e4e7e7')};
    }
    .CalendarDay__outside {
      border-bottom: 1px solid ${({ theme }) => (theme.isDark ? theme.colors.borderLight : '#e4e7e7')};
      border-top: 0px;
    }
  }
  & tr:last-of-type {
    border-right: 0px;
    .CalendarDay__outside {
      border-left: 1px solid ${({ theme }) => (theme.isDark ? theme.colors.borderLight : '#e4e7e7')};
    }
    .CalendarDay__outside ~ .CalendarDay__outside {
      border-left: 0px solid ${({ theme }) => (theme.isDark ? theme.colors.borderLight : '#e4e7e7')};
    }
  }
  .CalendarDay__selected {
    border-color: ${({ theme }) => (theme.isDark ? theme.colors.borderLight : '#e4e7e7')};
    border-right: 0px !important;
    border-top: 0 !important;
    .day-container {
      background-color: ${(props) => (props.isBuildVariant ? '#77A7FF' : ColorGreen)} !important;
    }
  }
  .DateRangePickerInput {
    align-items: center;
    background-color: ${(props) => (props.isBuildVariant ? 'transparent' : '#f5f5f5')};
    border: none;
    display: flex;
  }
  .DateRangePickerInput_arrow {
    ${(props) => (props.isBuildVariant ? 'padding: 0 12px;' : '')}
  }
  .DateInput {
    background-color: ${(props) => (props.isBuildVariant ? props.theme.colors.surfaceInput : 'transparent')};
    border: ${(props) => (props.isBuildVariant ? `1px solid ${props.theme.colors.borderLight}` : 'none')};
    border-radius: ${(props) => (props.isBuildVariant ? '4px' : 'none')};
  }
  .DateInput_input {
    background-color: transparent;
    border: none;
    color: ${({ theme }) => theme.colors.text};
    padding: ${(props) => (props.isBuildVariant ? '10px 12px' : '5px 8px')};
    text-align: center;
  }
  /* react-dates ships a white calendar. Inside Build read the theme so the popover
   * follows the mode; the standalone journal keeps the library's own look. The
   * values are plain props.theme reads, not nested arrows, so they compile. */
  ${(props) => (props.isBuildVariant ? `
    .DayPicker,
    .DayPicker__withBorder,
    .DayPicker_transitionContainer,
    .CalendarMonthGrid,
    .CalendarMonth {
      background: ${props.theme.colors.surfaceElevated};
    }
    .DayPicker__withBorder {
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    }
    .CalendarMonth_caption {
      color: ${props.theme.colors.text};
    }
    .DayPicker_weekHeader {
      color: ${props.theme.colors.textSecondary};
    }
    .CalendarDay__default {
      background: ${props.theme.colors.surfaceElevated};
      border-color: ${props.theme.colors.borderLight};
      color: ${props.theme.colors.text};
    }
    .CalendarDay__default:hover {
      background: ${props.theme.colors.surfaceSecondary};
    }
    .CalendarDay__blocked_out_of_range,
    .CalendarDay__blocked_out_of_range:hover {
      background: ${props.theme.colors.surfaceElevated};
      color: ${props.theme.colors.textTertiary};
    }
    .CalendarDay__selected_span,
    .CalendarDay__hovered_span,
    .CalendarDay__selected_span:hover,
    .CalendarDay__hovered_span:hover {
      background: rgba(119, 167, 255, 0.25);
      border-color: ${props.theme.colors.borderLight};
      color: ${props.theme.colors.text};
    }
    .DayPickerNavigation_button__default {
      background: ${props.theme.colors.surfaceElevated};
      border-color: ${props.theme.colors.borderMedium};
    }
    .DayPickerNavigation_button__default:hover {
      border-color: ${props.theme.colors.textSecondary};
    }
    .DayPickerNavigation_svg__horizontal {
      fill: ${props.theme.colors.textSecondary};
    }
    .DateInput_fangShape {
      fill: ${props.theme.colors.surfaceElevated};
    }
    .DateInput_fangStroke {
      stroke: ${props.theme.colors.borderLight};
    }
  ` : '')}
  .DateRangePicker_picker {
    z-index: 2;
  }
  & button {
    border: 1px solid ${(props) => props.theme.noBorderColor};
    color: ${(props) => props.theme.lightTextColor};
    font-weight: 600;
  }
  & .CalendarMonth_caption {
    font-family: 'Nunito Sans';
    font-weight: 300;
  }
`;

const DateErrorText = styled('div')`
  text-align: center;
  color: #ff6600;
  font-size: 12px;
  margin-top: 4px;
  width: 100%;
`;

const DateText = styled('div')`
  align-items: center;
  display: flex;
  font-size: 16px;
  margin-bottom: 8px;
  width: 100%;
`;

const ExerciseFilterContainer = styled('div')`
  width: 100%;
`;

const ExerciseText = styled('div')`
  align-items: center;
  display: flex;
  font-size: 16px;
  margin-bottom: 8px;
  width: 100%;
  @media screen and (max-width: 440px) {
    align-items: center;
  }
`;

const FiltersContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  padding: 0 30px;
  /* Phones: match the action row's inset so everything lines up edge to edge. */
  @media screen and (max-width: 767px) {
    margin-bottom: 16px;
    padding: 0 16px;
  }
`;

const MediaFilterContainer = styled('div')`
  align-items: center;
  display: flex;
  margin-bottom: 16px;
  width: 100%;
`;

const MediaText = styled('div')`
  align-items: center;
  display: flex;
  font-size: 16px;
  width: 75%;
`;

const MediaToggleContainer = styled('div')`
  align-items: center;
  display: flex;
  justify-content: end;
  width: 25%;
  .react-toggle--checked .react-toggle-track {
    background-color: ${(props) => (props.isBuildVariant ? '#77A7FF' : ColorGreen)};
  }
  .react-toggle--checked:hover .react-toggle-track:hover {
    background-color: ${(props) => (props.isBuildVariant ? '#77A7FF' : ColorGreen)};
  }
  .react-toggle-track {
    background-color: ${({ theme }) => (theme.isDark ? theme.colors.surfaceTertiary : '#c3c3c3')};
    height: 28px;
  }
  .react-toggle .react-toggle-track:hover {
    background-color: ${({ theme }) => (theme.isDark ? theme.colors.surfaceTertiary : '#c3c3c3')};
  }
  .react-toggle-thumb {
    border: none;
    height: 20px;
    left: ${(props) => (props.onlyMedia ? '' : '4px')};
    right: ${(props) => (props.onlyMedia ? '4px' : '')};
    top: 4px;
    width: 20px;
  }
  .react-toggle-thumb:hover .react-toggle-track {
    background-color: ${({ theme }) => (theme.isDark ? theme.colors.surfaceTertiary : '#c3c3c3')};
  }
`;

const ModalHeaderDiv = styled('div')`
  align-items: center;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  flex-direction: row;
  font-weight: 500;
  justify-content: flex-end;
  height: 75px;
  margin: 20px 0;
  padding: 10px 30px 0px;
  width: 100%;
  z-index: 1;
  ${(props) => (props.isBuildVariant ? `
    border-top-left-radius: ${BUILD_BORDER_RADIUS};
    border-top-right-radius: ${BUILD_BORDER_RADIUS};
    height: auto;
    margin: 0;
    padding: 20px 30px 16px;
  ` : '')}
  h2 {
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
    ${(props) => (props.isBuildVariant ? `
      font-family: 'Nunito Sans';
      font-size: 24px;
      font-weight: 700;
    ` : '')}
  }
  @media screen and (max-width: 767px) {
    height: auto;
    margin: 0;
    padding: 16px 16px 12px;
  }
`;

const ModalHeaderTitle = styled('div')`
  width: 50%;
`;

const SelectDateButton = styled('button')`
  align-items: center;
  background-color: ${(props) => (props.isBuildVariant ? 'transparent' : '#f5f5f5')};
  border: none;
  border-radius: ${(props) => (props.isBuildVariant ? '4px' : '10px')};
  color: ${({ theme }) => (theme.isDark ? theme.colors.textSecondary : '#a3a3a3')};
  display: flex;
  font-weight: 500;
  height: 48px;
  justify-content: ${(props) => (props.isBuildVariant ? 'flex-start' : 'center')};
  padding: ${(props) => (props.isBuildVariant ? '0' : '8px 12px')};
  width: 100%;
  &:not(:disabled) {
    cursor: default;
  }
  @media screen and (max-width: 440px) {
    padding: 0;
  }
`;

const SelectExerciseButton = styled('button')`
  align-items: center;
  background-color: ${(props) => (props.isBuildVariant ? props.theme.colors.surfaceElevated : '#f5f5f5')};
  border: ${(props) => (props.isBuildVariant ? `1px solid ${props.theme.colors.borderLight}` : 'none')};
  border-radius: ${(props) => (props.isBuildVariant ? '4px' : '10px')};
  color: ${(props) => (props.displayedExerciseName === 'Select an Exercise'
    ? '#a3a3a3'
    : props.theme.colors.text)};
  display: flex;
  font-weight: 500;
  height: 48px;
  padding: 8px 12px;
  width: 100%;
`;

const SelectExerciseText = styled('div')`
  align-items: center;
  display: flex;
  font-size: 19px;
  justify-content: left;
  padding-left: 20px;
  width: 90%;
  @media screen and (max-width: 440px) {
    padding-left: 0;
  }
`;

const FilterOptionsModal = ({ onRequestClose }) => {
  const { colors } = useTheme();
  const {
    dateRange, filterExercise, mediaOnly, tempExercise,
  } = useSelector(
    (state) => state.journal.data,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isBuildVariant = useJournalVariant() === 'build';
  const dropdownIconSize = isBuildVariant ? 12 : 16;
  // The two-month calendar is far wider than a phone screen, so drop to one.
  const windowWidth = useWindowSize().width;
  const isPhoneWidth = windowWidth <= 767;

  const [focusedRangeDateInput, setFocusedRangeDateInput] = useState(null);
  const [localFilterData, setLocalFilterData] = useState({
    endDate: dateRange?.endDate,
    onlyMedia: mediaOnly,
    selectedExercise: filterExercise,
    startDate: dateRange?.startDate,
  });
  const [dateError, setDateError] = useState('');
  const dispatch = useDispatch();

  // Get user's configured timezone, fallback to browser timezone
  const userTimeZone = currentUser?.userTimeZone || currentUser?.userSettings?.timeZone;
  const today = userTimeZone ? moment.tz(userTimeZone) : moment();

  const displayedExerciseName = (() => {
    const { name } = localFilterData.selectedExercise;
    if (name) {
      return name.length > 26 ? `${name.slice(0, 24)}...` : name;
    }
    return 'Select an Exercise';
  })();

  const submitButtonDisabled = (localFilterData.startDate && !localFilterData.endDate)
    || (!localFilterData.startDate && localFilterData.endDate);

  const handleClearAllFilters = () => {
    setLocalFilterData({
      endDate: '',
      onlyMedia: false,
      selectedExercise: '',
      startDate: '',
    });
    dispatch(setTempExercise({}));
    journalTracker('Filter options modal', 'Filter options cleared');
  };

  const handleDateChange = ({ startDate, endDate }) => {
    setLocalFilterData({
      ...localFilterData,
      endDate,
      startDate,
    });
    journalTracker('Filter options modal', 'Date range selected');
  };

  const handleOpenSelectExerciseModal = () => {
    dispatch(setSubActiveReduxModal('select-exercise-modal'));
    journalTracker('Filter options modal', 'Select exercise modal opened');
  };

  const handleSubmitFilters = () => {
    let exerciseToSubmit;
    if (tempExercise.name) {
      exerciseToSubmit = tempExercise;
    } else if (localFilterData.selectedExercise === '') {
      exerciseToSubmit = {};
    } else {
      exerciseToSubmit = filterExercise;
    }

    dispatch(
      setFilterOptions(
        localFilterData.onlyMedia,
        localFilterData.startDate,
        localFilterData.endDate,
        exerciseToSubmit || {},
      ),
    );
    dispatch(setActiveReduxModal(''));
    journalTracker('Filter options modal', 'Filters applied');
  };

  const handleToggleMediaOnly = () => {
    setLocalFilterData({
      ...localFilterData,
      onlyMedia: !localFilterData.onlyMedia,
    });
    journalTracker('Filter options modal', 'Media only toggle clicked');
  };

  useEffect(() => {
    if (tempExercise.name) {
      setLocalFilterData((prevData) => ({
        ...prevData,
        selectedExercise: tempExercise,
      }));
    }
  }, [tempExercise]);

  useEffect(() => {
    if (
      (localFilterData.startDate && !localFilterData.endDate)
      || (!localFilterData.startDate && localFilterData.endDate)
    ) {
      setDateError('Start and end dates must both be selected.');
    } else {
      setDateError('');
    }
  }, [localFilterData.startDate, localFilterData.endDate]);

  const renderHeader = () => (
    <ModalHeaderDiv isBuildVariant={isBuildVariant}>
      <ModalHeaderTitle>
        <h2>Filters</h2>
      </ModalHeaderTitle>
      <ClearFilters>
        <ClearFiltersButton
          isBuildVariant={isBuildVariant}
          onClick={handleClearAllFilters}
          aria-label='Clear all filters'
        >
          Clear All
        </ClearFiltersButton>
        {isBuildVariant && (
          <BuildCloseButton type='button' onClick={onRequestClose} aria-label='Close modal'>
            <IcomoonReact iconSet={iconSet} size={12} icon='remove' color={colors.text} />
          </BuildCloseButton>
        )}
      </ClearFilters>
    </ModalHeaderDiv>
  );

  const renderMediaFilter = () => (
    <MediaFilterContainer>
      <MediaText>
        {!isBuildVariant && (
          <IconContainer size='32px' marginRight='16px'>
            <IcomoonReact
              aria-hidden='true'
              iconSet={iconSet}
              size={32}
              icon='camera'
              color={colors.text}
            />
          </IconContainer>
        )}
        Media Only
      </MediaText>
      <MediaToggleContainer onlyMedia={localFilterData.onlyMedia} isBuildVariant={isBuildVariant}>
        <Toggle
          className='media-toggle'
          checked={localFilterData.onlyMedia}
          icons={false}
          onChange={handleToggleMediaOnly}
          aria-label='Toggle only showing entries with media'
        />
      </MediaToggleContainer>
    </MediaFilterContainer>
  );

  const renderDateFilter = () => (
    <DateFilterContainer>
      <DateText>
        {!isBuildVariant && (
          <IconContainer size='32px' marginRight='16px'>
            <IcomoonReact
              aria-hidden='true'
              iconSet={iconSet}
              size={32}
              icon='calendar'
              color={colors.text}
            />
          </IconContainer>
        )}
        Filter by Date Range
      </DateText>
      <SelectDateButton aria-label='Filter by date range' isBuildVariant={isBuildVariant}>
        <DatePickerContainer isBuildVariant={isBuildVariant}>
          <DateRangePicker
            startDate={localFilterData.startDate}
            startDateId='startDate'
            endDate={localFilterData.endDate}
            endDateId='endDate'
            onDatesChange={handleDateChange}
            focusedInput={focusedRangeDateInput}
            onFocusChange={setFocusedRangeDateInput}
            hideKeyboardShortcutsPanel
            isOutsideRange={(day) => day.isAfter(today, 'day')}
            numberOfMonths={isPhoneWidth ? 1 : 2}
          />
        </DatePickerContainer>
      </SelectDateButton>
      {dateError && <DateErrorText>{dateError}</DateErrorText>}
    </DateFilterContainer>
  );

  const renderExerciseFilter = () => (
    <ExerciseFilterContainer>
      <ExerciseText>
        {!isBuildVariant && (
          <IconContainer size='32px' marginRight='16px'>
            <IcomoonReact
              aria-hidden='true'
              iconSet={iconSet}
              size={32}
              icon='lift'
              color={colors.text}
            />
          </IconContainer>
        )}
        Filter by Associated Exercise
      </ExerciseText>
      <SelectExerciseButton
        onClick={handleOpenSelectExerciseModal}
        displayedExerciseName={displayedExerciseName}
        isBuildVariant={isBuildVariant}
        aria-label='Filter by associated exercise'
      >
        <SelectExerciseText>{displayedExerciseName}</SelectExerciseText>
        <IconContainer size={`${dropdownIconSize}px`}>
          <IcomoonReact
            aria-hidden='true'
            iconSet={iconSet}
            size={dropdownIconSize}
            icon='down-arrow'
            color={ColorGray}
          />
        </IconContainer>
      </SelectExerciseButton>
    </ExerciseFilterContainer>
  );

  const renderSubmitButton = () => (
    <ActionWrapper isBuildVariant={isBuildVariant}>
      {isBuildVariant ? (
        <BuildPrimaryButton
          type='button'
          onClick={handleSubmitFilters}
          disabled={submitButtonDisabled}
        >
          Apply Filters
        </BuildPrimaryButton>
      ) : (
        <NoHoverButton
          label='Submit'
          cta='Apply Filters'
          type='button'
          className='submit-button'
          onClick={handleSubmitFilters}
          color={ColorGreen}
          disabled={submitButtonDisabled}
        />
      )}
    </ActionWrapper>
  );

  const ModalPrimaryWrapper = isBuildVariant ? BuildPrimaryWrapper : PrimaryWrapper;

  return (
    <Wrapper>
      <ModalPrimaryWrapper padding='0px'>
        {!isBuildVariant && <CloseButtonContainer onRequestClose={onRequestClose} />}
        {renderHeader()}
        <FiltersContainer>
          {renderMediaFilter()}
          {renderDateFilter()}
          {renderExerciseFilter()}
        </FiltersContainer>
        {renderSubmitButton()}
      </ModalPrimaryWrapper>
    </Wrapper>
  );
};

FilterOptionsModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
};

export default FilterOptionsModal;
